import { Alert, Stack, Text } from 'braid-design-system';
import { SmartTextLink } from 'scoobie';

import { MarkdownPage } from '#src/templates/MarkdownPage.tsx';
import { isGeneratedPath } from '#src/utils/path.ts';

import { site } from './documentation.config.tsx';
import type { Page, PageGroup } from './page.ts';
import { mdxDocuments, tsxComponents } from './site.map.ts';
import type { Breadcrumb } from './components/Breadcrumbs/Breadcrumbs.tsx';
import { Helmet } from 'react-helmet';
import type React from 'react';

export interface PageNavigation {
  path: string;
  label: string;
  parent?: string;
}

export type FlatPage = Omit<Page, 'children'> & {
  parent?: {
    path?: string;
    label?: string;
  };
  prev?: PageNavigation;
  next?: PageNavigation;

  /**
   * Whether the page was directly annotated with `hidden: true`.
   *
   * The idea here is to only mark the topmost page in the tree as hidden then
   * enable navigation amongst its subtree pages without cross references to
   * unrelated hidden subtrees.
   */
  hiddenDirectly?: true;
};

export type RenderablePage = Omit<Page, 'path' | 'type'> & {
  path: string;
  type: NonNullable<Page['type']>;
};

const flattenPages = (pages: PageGroup[], parentHidden = false): FlatPage[] =>
  pages.reduce<FlatPage[]>((acc, { children, ...page }) => {
    if ('type' in page) {
      acc.push({
        ...page,
        ...(parentHidden || page.hidden ? { hidden: true } : {}),
        ...(page.hidden ? { hiddenDirectly: true } : {}),
      });
    }

    if (children !== undefined) {
      const newChildren = children.map((child) => ({
        ...child,
        parent: { path: page.path, label: page.label },
      }));
      acc.push(...flattenPages(newChildren, parentHidden || page.hidden));
    }

    return acc;
  }, []);

export const userCanViewPage = (
  page: FlatPage | Page,
  permissions: string[],
) => {
  if (page.permissions === undefined) {
    return true;
  }
  return page.permissions.every((permission) =>
    permissions.includes(permission),
  );
};

const isRenderablePage = (page: FlatPage): page is RenderablePage & FlatPage =>
  page.path !== undefined &&
  page.path.startsWith('/') &&
  page.type !== undefined;

/**
 * Check whether a pages path is part of the generated documentation
 */
export const isGeneratedPage = (page: FlatPage): boolean =>
  isGeneratedPath(page.path);

export const RENDERABLE_PAGES = Object.fromEntries(
  flattenPages(site)
    .filter(isRenderablePage)
    .filter(isGeneratedPage)
    .map((page, index, arr) => {
      let nextReset = false;
      const next = arr.slice(index + 1).find((p) => {
        if (!p.hidden) {
          return true;
        }

        if (p.hiddenDirectly) {
          // We've reached a different hidden subtree.
          // Ignore subsequent hidden pages.
          nextReset = true;
          return false;
        }

        return !nextReset && page.hidden;
      });

      let prevReset = page.hiddenDirectly;
      const prev = arr
        .slice(0, index)
        .reverse()
        .find((p) => {
          if (!p.hidden) {
            return true;
          }

          if (p.hiddenDirectly) {
            // We've reached the topmost page in our hidden subtree.
            // Ignore previous hidden pages that belong to a different subtree.
            prevReset = true;
            // But allow linking back to our own parent.
            return p.path === page.parent?.path;
          }

          return !prevReset && page.hidden;
        });

      return [
        page.path,
        {
          ...page,
          prev: prev
            ? {
                path: prev.path,
                label: prev.label,
                parent: prev.parent?.label,
              }
            : undefined,
          next: next
            ? {
                path: next.path,
                label: next.label,
                parent: next.parent?.label,
              }
            : undefined,
        },
      ];
    }),
);

const Banner = (page: {
  replacement: RenderablePage['replacement'];
  supersedes?: string;
  type: Exclude<RenderablePage['type'], 'reviewed' | 'standalone'>;
}): React.JSX.Element => {
  switch (page.type) {
    case 'archived':
      return (
        <Alert tone="caution">
          <Text>
            This content has been archived.{' '}
            {page.replacement ? (
              <>
                You may be looking for its{' '}
                <SmartTextLink href={page.replacement}>
                  latest revision
                </SmartTextLink>
                .
              </>
            ) : (
              'It may contain guidance that is no longer relevant.'
            )}
          </Text>
        </Alert>
      );

    case 'draft':
      return (
        <Alert tone="info">
          <Text>This content is still in draft.</Text>
        </Alert>
      );

    case 'unstable':
      return (
        <Alert tone="caution">
          <Stack space="medium">
            <Text>
              This content provides an early look at an upcoming extension to
              the SEEK API. You may use it to scope integration effort, but the
              features described are not fully implemented and may be adjusted
              ahead of a production release.
            </Text>

            <Text>
              Check with your SEEK contact before starting any build work
              described here
              {page.supersedes ? (
                <>
                  , or refer to our{' '}
                  <SmartTextLink href={page.supersedes}>
                    prior documentation
                  </SmartTextLink>{' '}
                  for in-flight or existing integrations
                </>
              ) : null}
              .
            </Text>
          </Stack>
        </Alert>
      );
  }
};

const BY_REPLACEMENT = Object.fromEntries(
  Object.entries(RENDERABLE_PAGES).map(([path, page]) => [
    page.replacement,
    path,
  ]),
);

const getBreadcrumbs = (page: { path?: string }): Breadcrumb[] | null => {
  if (page.path === undefined) {
    return null;
  }

  const currentPage = RENDERABLE_PAGES[page.path];
  if (currentPage === undefined) {
    return null;
  }

  return [
    ...(currentPage.parent ? (getBreadcrumbs(currentPage.parent) ?? []) : []),
    {
      title: currentPage.label,
      href: currentPage.path,
    },
  ];
};

export const renderPage = (page: RenderablePage) => {
  const currentPage = RENDERABLE_PAGES[page.path];
  const next = currentPage?.next;
  const prev = currentPage?.prev;
  const baseBreadcrumbs = getBreadcrumbs(page);
  const breadcrumbs = baseBreadcrumbs
    ? [{ title: 'Docs', href: '/' }, ...baseBreadcrumbs]
    : null;

  switch (page.type) {
    case 'archived':
    case 'draft':
    case 'reviewed':
    case 'unstable':
      const mdxDocument = mdxDocuments[page.path];

      if (mdxDocument === undefined) {
        throw Error(`${page.path} does not have an associated component`);
      }

      const { toc, document } = mdxDocument;

      const supersededPage = BY_REPLACEMENT[page.path];

      const banner =
        page.type === 'reviewed' ? null : (
          <Banner
            replacement={page.replacement}
            supersedes={supersededPage}
            type={page.type}
          />
        );

      return (
        <>
          {page.type === 'archived' || page.hidden ? (
            // Hidden pages are not meant to be user-findable.
            // Archived pages are user-findable, under an 'archived' menu section,
            // but offer limited value to users who would be stumbling upon them via Google.

            <Helmet>
              <meta name="robots" content="noindex" />
            </Helmet>
          ) : null}

          <MarkdownPage
            banner={banner}
            breadcrumbs={breadcrumbs}
            document={document}
            label={page.label}
            next={next}
            prev={prev}
            toc={toc}
          />
        </>
      );

    case 'standalone':
      const Component = tsxComponents[page.path];

      if (Component === undefined) {
        throw Error(`${page.path} does not have an associated component`);
      }

      return <Component next={next} prev={prev} breadcrumbs={breadcrumbs} />;
  }
};
